import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, UntypedFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { UnivPrefsService } from '_services/univ-prefs.service';
import { PublishingService } from '_services/publishing.service';
import { HelpComponent } from '../../_shared/help/help.component';
import { MatButton } from '@angular/material/button';
import { MatStepper, MatStep, MatStepLabel } from '@angular/material/stepper';
import { QuillEditorComponent } from 'ngx-quill';
import { Subscription } from 'rxjs';
import { take, tap, filter } from 'rxjs/operators';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { AsyncPipe } from '@angular/common';
import { QuillA11yDirective } from '../../_shared/directives/quill-a11y.directive';

type ContentGroupValue = {
  welcomeIntro: string | null;
  welcomeDisclaimer: string | null;
  nextSteps: string | null;
  schoolQuestionHeader: string | null;
};

@Component({
  selector: 'app-content',
  templateUrl: './content.component.html',
  styleUrls: ['./content.component.css'],
  imports: [HelpComponent, MatButton, ReactiveFormsModule, MatStepper, MatStep, MatStepLabel, QuillEditorComponent, QuillA11yDirective, AsyncPipe]
})
export class ContentComponent implements OnInit, OnDestroy {
  @ViewChild(MatStepper) stepper?: MatStepper;

  contentForm: UntypedFormGroup;
  state: 'loading' | 'synced' | 'modified' | 'error' = 'loading';
  schoolId: string;
  currentStepIndex = 0;
  isApplyingDraft = false;
  isResettingDraft = false;
  statusMessage = '';
  errorMessage = '';
  private formSub?: Subscription;
  modules = {
    clipboard: {
      matchVisual: false
    },
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],        // toggled buttons

      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent

      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

      [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
      [{ 'align': [] }],

      ['clean'],                                         // remove formatting button

      ['link', 'image', 'video']                         // link and image, video
    ]
  };

  constructor(
    private titleService: Title,
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    public ups: UnivPrefsService,
    private publishingService: PublishingService,
    private announcer: LiveAnnouncer
  ) {
    this.titleService.setTitle('Content' + ' | NPC Admin');
    this.contentForm = this.fb.group({
      content: new UntypedFormGroup({
        welcomeIntro: new UntypedFormControl(),
        welcomeDisclaimer: new UntypedFormControl(),
        nextSteps: new UntypedFormControl(),
        schoolQuestionHeader: new UntypedFormControl()
      })
    });
    this.schoolId = this.route.snapshot.parent?.paramMap.get('schoolId') ?? '';
  }

  ngOnInit() {
    this.loadData();
    this.trackFormChanges();
  }

  loadData() {
    this.state = 'loading';
    this.ups.draftPrefs
      .pipe(
        filter(prefs => prefs !== null), // Wait for data to be loaded
        tap(prefs => {
          if (prefs.content) {
            // Patch individual controls to ensure Quill editor properly initializes
            // Use emitEvent: false to prevent triggering modified state during load
            const fg: UntypedFormGroup = this.contentForm.get(['content']) as UntypedFormGroup;
            for (const field in fg.controls) {
              if (prefs.content[field] !== undefined) {
                const fc: UntypedFormControl = fg.get(field) as UntypedFormControl;
                fc.patchValue(prefs.content[field], { emitEvent: false });
              }
            }
          }
          this.contentForm.markAsPristine();
          this.state = 'synced';
        }),
        take(1)
      )
      .subscribe();
  }

  trackFormChanges() {
    this.formSub = this.contentForm.valueChanges
      .pipe(
        tap(() => {
          if (this.state !== 'loading') {
            this.state = 'modified';
          }
        })
      )
      .subscribe();
  }

  async save() {
    console.log('Save called - Form valid:', this.contentForm.valid, 'State:', this.state);
    console.log('Form value:', this.contentForm.value);

    if (!this.contentForm.valid || this.state !== 'modified') {
      console.log('Save aborted - Form invalid or state not modified');
      return;
    }

    try {
      const contentToSave = {
        content: this.normalizeContentSpaces(this.contentForm.value.content as ContentGroupValue)
      };
      console.log('Saving content:', contentToSave);
      await this.ups.savePreferences(contentToSave, true);
      this.contentForm.markAsPristine();
      this.state = 'synced';
      console.log('Save successful');
    } catch (err) {
      console.error('Save error:', err);
      this.state = 'error';
    }
  }

  private normalizeContentSpaces(content?: ContentGroupValue | null): ContentGroupValue {
    if (!content) {
      return {
        welcomeIntro: null,
        welcomeDisclaimer: null,
        nextSteps: null,
        schoolQuestionHeader: null
      };
    }
    const normalized: ContentGroupValue = { ...content };
    (Object.keys(normalized) as (keyof ContentGroupValue)[]).forEach(key => {
      const value = normalized[key];
      normalized[key] = typeof value === 'string' ? this.replaceNbsp(value) : value;
    });
    return normalized;
  }

  private replaceNbsp(value: string): string {
    if (!value) {
      return value;
    }
    return value.replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ');
  }

  ngOnDestroy() {
    if (this.formSub) {
      this.formSub.unsubscribe();
    }
  }
  async goToNext(): Promise<void> {
    if (this.contentForm.valid && this.state === 'modified') {
      await this.save();
    }
    if (this.stepper && this.currentStepIndex < this.stepper.steps.length - 1) {
      this.currentStepIndex++;
      this.stepper.next();
    }
  }

  async goToPrevious(): Promise<void> {
    if (this.contentForm.valid && this.state === 'modified') {
      await this.save();
    }
    if (this.stepper && this.currentStepIndex > 0) {
      this.currentStepIndex--;
      this.stepper.previous();
    }
  }

  get canGoBack(): boolean {
    return this.currentStepIndex > 0;
  }

  get canGoNext(): boolean {
    return this.stepper && this.currentStepIndex < this.stepper.steps.length - 1;
  }

  onStepperSelectionChange(index: number): void {
    this.currentStepIndex = index;
    // Fixes issue with Chrome where HTML formatting is stripped when using material stepper
    const fg: UntypedFormGroup = this.contentForm.get(['content']) as UntypedFormGroup;
    if (!fg) return;

    const fields = Object.keys(fg.controls);
    if (fields[index]) {
      const fc: UntypedFormControl = fg.get(fields[index]) as UntypedFormControl;
      const prefs = this.ups.draftPrefs.value;
      if (prefs && prefs.content && prefs.content[fields[index]]) {
        fc.patchValue(prefs.content[fields[index]], { emitEvent: false });
      }
    }
  }

  async makeChangesLive(): Promise<void> {
    if (this.isApplyingDraft || !this.ups.draftChanges.value) {
      return;
    }
    this.isApplyingDraft = true;
    this.errorMessage = '';
    this.statusMessage = 'Applying draft settings to live...';
    this.announcer.announce('Applying draft settings to live.');
    try {
      await this.publishingService.publishDraft(this.schoolId);
      this.statusMessage = 'Draft settings are now live.';
      this.announcer.announce('Draft settings are now live.');
    } catch (error) {
      console.error('Failed to apply draft settings to live', error);
      this.errorMessage = 'Unable to make draft settings live. Please try again.';
      this.statusMessage = 'Action failed.';
      this.announcer.announce('Unable to make draft settings live.');
    } finally {
      this.isApplyingDraft = false;
    }
  }

  async resetDraftToLive(): Promise<void> {
    if (this.isResettingDraft) {
      return;
    }
    this.isResettingDraft = true;
    this.errorMessage = '';
    this.statusMessage = 'Restoring live settings to draft...';
    this.announcer.announce('Restoring live settings to draft.');
    try {
      await this.publishingService.copyToDraft(this.schoolId);
      // copyToDraft triggers a full reload when successful.
    } catch (error) {
      console.error('Failed to reset draft settings', error);
      this.errorMessage = 'Unable to reset draft settings. Please try again.';
      this.statusMessage = 'Action failed.';
      this.announcer.announce('Unable to reset draft settings.');
    } finally {
      this.isResettingDraft = false;
    }
  }

}
